// WP-1.11 (S-05 `/`, M16 DB-1..5): answers the five dashboard questions at a glance.
// Every aggregate is scoped to one currency (matching the engine's own `queryDashboard`
// discipline — the engine never auto-converts) with a picker when the owner has accounts in
// more than one. AC-5's own consolidated cross-currency total (below, `queryConsolidatedNetWorth`)
// is the one exception: an explicit, separately-computed figure using whatever indicative FX
// rates the owner has already cached, never a live conversion this app has no provider for.
import type { CurrencyCode } from '@pfos/engine';
import { ShieldAlert, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Amount } from '../components/Amount';
import { selectClass } from '../components/FormKit';
import { useAccounts } from '../lib/accounts';
import { useConsolidatedNetWorth, useDashboard } from '../lib/dashboard';
import { useRampUp } from '../lib/goals';
import { useSettings } from '../lib/settings';
import { useTransactions } from '../lib/transactions';

function monthRange(monthOffset: number): { from: string; to: string } {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth() + monthOffset;
  return {
    from: new Date(Date.UTC(year, month, 1)).toISOString(),
    to: new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 999)).toISOString(),
  };
}

function Tile({
  label,
  minorUnits,
  currency,
  onClick,
}: {
  label: string;
  minorUnits: number;
  currency: CurrencyCode;
  onClick?: () => void;
}): React.JSX.Element {
  const content = (
    <>
      <p className="text-xs uppercase tracking-wide text-muted">{label}</p>
      <Amount currency={currency} minorUnits={minorUnits} className="text-lg font-semibold" />
    </>
  );
  if (onClick === undefined) {
    return (
      <div className="rounded-[var(--radius-card)] border border-border bg-surface p-3">
        {content}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-[var(--radius-card)] border border-border bg-surface p-3 text-left hover:bg-bg"
    >
      {content}
    </button>
  );
}

function BlockRow({
  label,
  minorUnits,
  currency,
}: {
  label: string;
  minorUnits: number;
  currency: CurrencyCode;
}): React.JSX.Element {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted">{label}</span>
      <Amount currency={currency} minorUnits={minorUnits} />
    </div>
  );
}

function Block({
  title,
  onClick,
  children,
}: {
  title: string;
  onClick?: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
      {onClick === undefined ? (
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted">{title}</p>
      ) : (
        <button
          type="button"
          onClick={onClick}
          className="mb-2 text-xs font-medium uppercase tracking-wide text-muted hover:text-ink"
        >
          {title}
        </button>
      )}
      <div className="space-y-1">{children}</div>
    </div>
  );
}

export function DashboardPage(): React.JSX.Element {
  const { data: accounts } = useAccounts();
  const navigate = useNavigate();
  const currencies = Array.from(new Set((accounts ?? []).map((a) => a.currency)));
  const [currency, setCurrency] = useState<CurrencyCode>('PKR');
  const [periodOffset, setPeriodOffset] = useState(0); // 0 = this month, -1 = last month
  const period = monthRange(periodOffset);
  const { data: snapshot, isLoading } = useDashboard(currency, period);
  const { data: recentTx } = useTransactions({});
  const { data: rampUp } = useRampUp(currency);
  const { data: settings } = useSettings();
  const baseCurrency = (
    typeof settings?.baseCurrency === 'string' ? settings.baseCurrency : 'PKR'
  ) as CurrencyCode;
  // AC-5: only worth asking for when the owner actually holds more than one currency.
  const { data: consolidated } = useConsolidatedNetWorth(baseCurrency, currencies.length > 1);
  const missingRateCurrencies = (consolidated?.perCurrency ?? []).filter(
    (c) => c.convertedMinor === null,
  );

  const strip = (accounts ?? []).filter((a) => a.currency === currency && !a.archived);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold text-ink">Dashboard</h1>
        <div className="flex flex-wrap items-center gap-2">
          {currencies.length > 1 && (
            <select
              aria-label="Currency"
              className={`${selectClass} w-auto`}
              value={currency}
              onChange={(e) => {
                setCurrency(e.target.value as CurrencyCode);
              }}
            >
              {currencies.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          )}
          <div className="flex gap-1" role="radiogroup" aria-label="Period">
            {[
              { label: 'This month', offset: 0 },
              { label: 'Last month', offset: -1 },
            ].map((opt) => (
              <button
                key={opt.label}
                type="button"
                role="radio"
                aria-checked={periodOffset === opt.offset}
                onClick={() => {
                  setPeriodOffset(opt.offset);
                }}
                className={`rounded-[var(--radius-control)] border px-3 py-1.5 text-sm ${
                  periodOffset === opt.offset
                    ? 'border-primary bg-primary/10 font-medium text-primary'
                    : 'border-border text-ink hover:bg-bg'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* AC-5: a consolidated cross-currency total, using whatever FX rates the owner has
          already set (Settings > Financial, or inline on a cross-currency transfer) — never a
          live/guessed rate. Only shown once there's actually more than one currency to
          consolidate. */}
      {currencies.length > 1 && consolidated !== undefined && (
        <div className="mb-4 rounded-[var(--radius-card)] border border-border bg-surface p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-muted">
            Total net worth (consolidated, {baseCurrency})
          </p>
          <Amount
            currency={consolidated.baseCurrency}
            minorUnits={consolidated.totalMinor}
            className="text-xl font-semibold"
          />
          {missingRateCurrencies.length > 0 && (
            <p className="mt-1 text-xs text-muted">
              Not included: {missingRateCurrencies.map((c) => c.currency).join(', ')} — no exchange
              rate set yet.{' '}
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={() => {
                  navigate('/settings/financial');
                }}
              >
                Set one
              </button>
            </p>
          )}
        </div>
      )}

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {snapshot !== undefined && (
        <>
          {/* GL-2 (LOCKED): "bind the Spendable-Now computation and force advisor warnings on
              violation" — a real warning, danger-colored, not a soft hint. */}
          {snapshot.strictViolation.violated && (
            <div
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-[var(--radius-card)] border border-danger/30 bg-danger/10 p-4 text-sm text-danger"
            >
              <ShieldAlert size={18} className="mt-0.5 shrink-0" />
              <p>
                Strict goals require{' '}
                <Amount
                  currency={snapshot.strictViolation.currency}
                  minorUnits={snapshot.strictViolation.requiredMinor}
                />{' '}
                kept available, but only{' '}
                <Amount
                  currency={snapshot.strictViolation.currency}
                  minorUnits={snapshot.strictViolation.availableMinor}
                />{' '}
                is available (GL-2). Review your strict goals or reserves.
              </p>
            </div>
          )}

          {/* GL-8: the alert being "active" just means the feature turned on (>= 2 months of
              data); it only actually fires once Spendable Now dips below the computed/overridden
              threshold — hence the extra spendableNowMinor comparison, not lowSpendableAlertActive
              alone. Warning-colored, not danger — a heads-up, not a hard rule broken. */}
          {rampUp !== undefined &&
            rampUp.lowSpendableAlertActive &&
            snapshot.spendableNowMinor < rampUp.lowSpendableThresholdMinor && (
              <div
                role="alert"
                className="mb-4 flex items-start gap-2 rounded-[var(--radius-card)] border border-warning/30 bg-warning/10 p-4 text-sm text-warning"
              >
                <TriangleAlert size={18} className="mt-0.5 shrink-0" />
                <p>
                  Spendable Now is below your low-Spendable threshold of{' '}
                  <Amount
                    currency={snapshot.currency}
                    minorUnits={rampUp.lowSpendableThresholdMinor}
                  />{' '}
                  (GL-8). Consider slowing discretionary spending or topping up.
                </p>
              </div>
            )}

          <div className="mb-4 rounded-[var(--radius-card)] border border-border bg-surface p-6">
            <p className="text-xs font-medium uppercase tracking-wide text-muted">Spendable Now</p>
            <Amount
              currency={snapshot.currency}
              minorUnits={snapshot.spendableNowMinor}
              className="text-3xl font-semibold"
            />
            <p className="mt-1 text-xs text-muted" data-testid="spendable-formula">
              available <Amount currency={snapshot.currency} minorUnits={snapshot.availableMinor} />{' '}
              − reserved <Amount currency={snapshot.currency} minorUnits={snapshot.reservedMinor} />{' '}
              − holding{' '}
              <Amount currency={snapshot.currency} minorUnits={snapshot.holdingForOthersMinor} />
            </p>
          </div>

          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Tile
              label="In-Transit"
              minorUnits={snapshot.inTransitMinor}
              currency={snapshot.currency}
              onClick={() => {
                navigate('/tx');
              }}
            />
            <Tile
              label="Holding for others"
              minorUnits={snapshot.holdingForOthersMinor}
              currency={snapshot.currency}
              onClick={() => {
                navigate('/tpf');
              }}
            />
            <Tile
              label="Reserved"
              minorUnits={snapshot.reservedMinor}
              currency={snapshot.currency}
            />
            <Tile
              label="Net position"
              minorUnits={snapshot.netPositionMinor}
              currency={snapshot.currency}
            />
          </div>

          <div className="mb-4 grid gap-3 sm:grid-cols-2">
            <Block
              title="Coming"
              onClick={() => {
                navigate('/recurring');
              }}
            >
              <BlockRow
                label="Upcoming inflow (30d)"
                minorUnits={snapshot.upcomingRecurringInflowMinor}
                currency={snapshot.currency}
              />
              <BlockRow
                label="Upcoming bills (30d)"
                minorUnits={snapshot.upcomingRecurringBillsMinor}
                currency={snapshot.currency}
              />
            </Block>
            <Block
              title="I owe"
              onClick={() => {
                navigate('/owed');
              }}
            >
              <BlockRow
                label="Total payable"
                minorUnits={snapshot.payableTotalMinor}
                currency={snapshot.currency}
              />
              <BlockRow
                label="Due soon"
                minorUnits={snapshot.payableDueSoonMinor}
                currency={snapshot.currency}
              />
            </Block>
            <Block
              title="Owed to me"
              onClick={() => {
                navigate('/owed');
              }}
            >
              <BlockRow
                label="Total receivable"
                minorUnits={snapshot.receivableTotalMinor}
                currency={snapshot.currency}
              />
              <BlockRow
                label="Overdue"
                minorUnits={snapshot.receivableOverdueMinor}
                currency={snapshot.currency}
              />
            </Block>
            <Block title="This period">
              <BlockRow
                label="Gross income"
                minorUnits={snapshot.grossIncomeMinor}
                currency={snapshot.currency}
              />
              <BlockRow
                label="Expenses"
                minorUnits={snapshot.expensesMinor}
                currency={snapshot.currency}
              />
              <BlockRow label="Fees" minorUnits={snapshot.feesMinor} currency={snapshot.currency} />
              <BlockRow
                label="Net cash flow"
                minorUnits={snapshot.netCashFlowMinor}
                currency={snapshot.currency}
              />
            </Block>
          </div>
        </>
      )}

      {strip.length > 0 && (
        <div className="mb-4 flex gap-3 overflow-x-auto pb-1">
          {strip.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => {
                navigate(`/accounts/${a.id}`);
              }}
              className="shrink-0 rounded-[var(--radius-card)] border border-border bg-surface p-3 text-left hover:bg-bg"
            >
              <p className="text-xs text-muted">{a.name}</p>
              <Amount currency={a.currency} minorUnits={a.balanceMinor} className="font-medium" />
            </button>
          ))}
        </div>
      )}

      <div className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted">
          Recent activity
        </p>
        {recentTx === undefined || recentTx.length === 0 ? (
          <p className="text-sm text-muted">No transactions yet.</p>
        ) : (
          <ul className="space-y-2">
            {recentTx.slice(0, 10).map((tx) => (
              <li key={tx.id} className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  className="text-ink hover:underline"
                  onClick={() => {
                    navigate(`/tx/${tx.id}`);
                  }}
                >
                  {tx.category ?? tx.party ?? '—'}
                </button>
                <Amount
                  currency={tx.currency}
                  minorUnits={tx.type === 'expense' ? -tx.amountMinor : tx.amountMinor}
                  className={tx.type === 'expense' ? 'text-danger' : 'text-ink'}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
